import { useState } from "react";
import { Calendar, Clock } from "lucide-react";
import { Link } from "react-router-dom";
import ComingSoonModal from "../ComingSoonModal";


export default function Insights({ articles = [], isLoading }) {
  const [comingSoonModal, setComingSoonModal] = useState({
    isOpen: false,
    featureName: "",
  });

  const handleComingSoon = (e, feature) => {
    e.preventDefault();
    setComingSoonModal({ isOpen: true, featureName: feature });
  };


  return (
    <section
      className="py-20 md:py-28 px-6 bg-[#FAFAFA] dark:bg-[#1A1A1A]"
    >
      <div className="max-w-[1400px] mx-auto">
        {/* Section Header */}
        <div className="mb-16">
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-6">
            <div>
              <h2
                className="text-black dark:text-white font-medium font-serif leading-tight mb-3"
                style={{
                  fontSize: "clamp(32px, 5vw, 56px)",
                }}
              >
                Expert Insights
              </h2>
              <p className="text-[#666666] dark:text-[#A0A0A0] text-lg md:text-xl max-w-2xl">
                Event planning tips, industry trends, and rental guides from our
                team
              </p>
            </div>
            <Link
              to="#"
              onClick={(e) => handleComingSoon(e, "Insights Blog")}
              className="inline-flex items-center gap-2 text-[#62397f] dark:text-[#A78BFA] font-semibold hover:gap-3 transition-all duration-200"
            >
              View All Articles
              <svg
                width="20"
                height="20"
                viewBox="0 0 20 20"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M7.5 15L12.5 10L7.5 5"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </Link>
          </div>
        </div>

        {/* Loading Skeleton */}
        {isLoading && (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {[...Array(3)].map((_, i) => (
              <div
                key={i}
                className="animate-pulse bg-white dark:bg-[#1E1E1E] rounded-2xl overflow-hidden border border-[#E9E9E9] dark:border-[#333333] h-[480px]"
              >
                <div className="h-56 bg-[#E0E0E0] dark:bg-[#2A2A2A]" />
                <div className="p-6">
                  <div className="h-6 bg-[#E0E0E0] dark:bg-[#2A2A2A] rounded w-full mb-4" />
                  <div className="h-6 bg-[#E0E0E0] dark:bg-[#2A2A2A] rounded w-3/4 mb-6" />
                  <div className="h-4 bg-[#E0E0E0] dark:bg-[#2A2A2A] rounded w-full mb-2" />
                  <div className="h-4 bg-[#E0E0E0] dark:bg-[#2A2A2A] rounded w-full mb-2" />
                  <div className="h-4 bg-[#E0E0E0] dark:bg-[#2A2A2A] rounded w-2/3 mb-8" />
                  <div className="flex gap-4">
                    <div className="h-3 bg-[#E0E0E0] dark:bg-[#2A2A2A] rounded w-1/4" />
                    <div className="h-3 bg-[#E0E0E0] dark:bg-[#2A2A2A] rounded w-1/4" />
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Empty State */}
        {!isLoading && articles.length === 0 && (
          <div className="text-center py-24 bg-white dark:bg-[#1E1E1E] rounded-3xl border border-[#E9E9E9] dark:border-[#333333] shadow-sm">
            <div className="mb-6 text-[#62397f]/20">
              <Calendar size={80} className="mx-auto" />
            </div>
            <h3 className="font-serif text-2xl font-bold text-black dark:text-white mb-2">
              No Articles Yet
            </h3>
            <p className="text-[#666666] dark:text-[#A0A0A0] max-w-md mx-auto mb-8">
              Our team is busy writing the latest tips and trends. Check back
              soon for expert insights!
            </p>
          </div>
        )}

        {/* Articles Grid */}
        {!isLoading && articles.length > 0 && (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {articles.map((article, index) => (
              <Link
                key={index}
                to="#"
                onClick={(e) => handleComingSoon(e, article.title)}
                className="group bg-white dark:bg-[#1E1E1E] rounded-2xl overflow-hidden border border-[#E9E9E9] dark:border-[#333333] transition-all duration-300 hover:border-[#62397f] dark:hover:border-[#A78BFA] hover:shadow-[0_12px_32px_rgba(139,92,246,0.15)] dark:hover:shadow-[0_12px_32px_rgba(0,0,0,0.3)] transform hover:-translate-y-2"
              >
                {/* Featured Image */}
                <div className="relative h-56 overflow-hidden bg-[#F5F5F5] dark:bg-[#2A2A2A]">
                  <img
                    src={article.image}
                    alt={article.title}
                    className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/20 to-transparent"></div>
                  {/* Category Badge */}
                  <div className="absolute top-4 left-4 px-3 py-1 bg-[#62397f] text-white text-xs font-bold uppercase tracking-wider rounded-full">
                    {article.category}
                  </div>
                </div>
                {/* Content */}
                <div className="p-6">
                  <h3 className="text-xl font-bold text-black dark:text-white mb-3 leading-tight group-hover:text-[#62397f] dark:group-hover:text-[#A78BFA] transition-colors duration-200">
                    {article.title}
                  </h3>
                  <p className="text-[#666666] dark:text-[#A0A0A0] text-sm leading-relaxed mb-4">
                    {article.excerpt}
                  </p>
                  {/* Meta Info */}
                  <div className="flex items-center gap-4 text-xs text-[#999999] dark:text-[#666666]">
                    <div className="flex items-center gap-1">
                      <Calendar size={14} />
                      <span>{article.date}</span>
                    </div>
                    <div className="flex items-center gap-1">
                      <Clock size={14} />
                      <span>{article.readTime}</span>
                    </div>
                  </div>
                </div>
                {/* Hover Indicator */}
                <div className="h-1 bg-gradient-to-r from-[#62397f] to-[#7C3AED] transform scale-x-0 group-hover:scale-x-100 transition-transform duration-300 origin-left"></div>
              </Link>
            ))}
          </div>
        )}
      </div>

      <ComingSoonModal
        isOpen={comingSoonModal.isOpen}
        onClose={() => setComingSoonModal({ isOpen: false, featureName: "" })}
        featureName={comingSoonModal.featureName}
      />
    </section>
  );
}
